import React, { useEffect, useState, useRef } from "react";
import { api, wsUrl } from "@/lib/api";
import { getRank } from "@/lib/ranks";
import { Users, Hand, Swords, Plus, X, Search } from "lucide-react";
import { toast } from "sonner";

export const QueuePanel = ({ compact = false }) => {
  const [queue, setQueue] = useState([]);
  const [tables, setTables] = useState([]);
  const [players, setPlayers] = useState([]);
  const [pairing, setPairing] = useState(null);
  const wsRef = useRef(null);

  const load = () => api.get("/queue").then(({ data }) => setQueue(data));

  useEffect(() => {
    load();
    api.get("/tables").then(({ data }) => setTables(data));
    api.get("/players?limit=500").then(({ data }) => setPlayers(data));

    let ws;
    try {
      ws = new WebSocket(wsUrl("/api/ws/tv"));
      wsRef.current = ws;
      ws.onmessage = (e) => {
        try {
          const ev = JSON.parse(e.data);
          if (ev.type === "queue.join") {
            toast.info(`Hàng chờ: ${ev.item.player_nickname} muốn chơi Bàn ${ev.item.table_number}`);
            load();
          } else if (["queue.leave", "queue.dismiss", "queue.refresh", "match.start"].includes(ev.type)) {
            load();
          }
        } catch {}
      };
    } catch {}
    return () => { if (ws) ws.close(); };
  }, []);

  const dismiss = async (q) => {
    await api.post(`/queue/${q.id}/dismiss`);
    load();
  };

  // Group by table
  const byTable = {};
  for (const q of queue) {
    if (!byTable[q.table_id]) byTable[q.table_id] = { table_number: q.table_number, table_id: q.table_id, items: [] };
    byTable[q.table_id].items.push(q);
  }
  const groups = Object.values(byTable).sort((a, b) => a.table_number - b.table_number);

  if (compact && queue.length === 0) return null;

  return (
    <div className="rounded-xl bg-[#0c0d14] border border-white/8 p-5" data-testid="admin-queue-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-semibold flex items-center gap-2">
          <Hand size={16} className="text-cyan-300" /> Hàng chờ
          <span className="text-xs text-white/40 font-normal">· {queue.length} người</span>
        </h2>
      </div>
      {queue.length === 0 ? (
        <div className="text-white/40 text-sm py-6 text-center">Chưa có yêu cầu</div>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.table_id} className="rounded-md bg-white/[0.02] border border-white/5 p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-cyan-300">Bàn {g.table_number} · {g.items.length} chờ</div>
                {g.items.length >= 2 && (
                  <button onClick={() => setPairing({ table: tables.find((t) => t.id === g.table_id), items: g.items })}
                    data-testid={`quick-match-${g.table_number}`}
                    className="px-2.5 py-1 rounded text-xs bg-cyan-400/15 text-cyan-300 border border-cyan-400/30 hover:bg-cyan-400/25 flex items-center gap-1">
                    <Swords size={11} /> Ghép trận
                  </button>
                )}
              </div>
              <div className="space-y-1.5">
                {g.items.map((q, i) => (
                  <div key={q.id} className="flex items-center gap-2.5">
                    <div className="w-5 text-center text-xs font-display font-bold text-white/50">#{i + 1}</div>
                    <img src={q.player_avatar} alt="" className="w-7 h-7 rounded-full" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{q.player_nickname}</div>
                      <div className="text-[10px]" style={{ color: getRank(q.player_elo).color }}>{q.player_rank} · {q.player_elo}</div>
                    </div>
                    <button onClick={() => dismiss(q)} className="p-1.5 rounded text-white/40 hover:text-red-400 hover:bg-red-500/10" data-testid={`dismiss-${q.id}`}>
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {pairing && <PairModal pairing={pairing} players={players} onClose={() => setPairing(null)} onDone={() => { setPairing(null); load(); }} />}
    </div>
  );
};

const PairModal = ({ pairing, players, onClose, onDone }) => {
  const queueItems = pairing.items;
  const [a, setA] = useState(queueItems[0]?.player_id);
  const [b, setB] = useState(queueItems[1]?.player_id);
  const [bo, setBo] = useState(1);
  const [mode, setMode] = useState("rank");

  const submit = async () => {
    if (!a || !b || a === b) return toast.error("Chọn 2 người khác nhau");
    try {
      await api.post("/matches", { player_a_id: a, player_b_id: b, table_id: pairing.table.id, best_of: Number(bo), mode });
      toast.success(`Đã tạo trận tại Bàn ${pairing.table.number}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lỗi");
    }
  };

  const pickList = queueItems.map((q) => ({ id: q.player_id, nickname: q.player_nickname, elo: q.player_elo, avatar: q.player_avatar, rank: q.player_rank, _queue: true }));
  // include other players too for flexibility
  const others = players.filter((p) => !pickList.find((x) => x.id === p.id)).map((p) => ({ id: p.id, nickname: p.nickname, elo: p.elo, avatar: p.avatar_url, rank: p.rank, _queue: false }));
  const all = [...pickList, ...others];

  const Pick = ({ value, onChange, exclude, testid }) => (
    <div className="rounded-md bg-white/[0.03] border border-white/10 p-2 max-h-48 overflow-y-auto" data-testid={testid}>
      {all.filter((p) => p.id !== exclude).map((p) => (
        <button key={p.id} onClick={() => onChange(p.id)}
          className={`w-full flex items-center gap-2 p-1.5 rounded text-left ${value === p.id ? "bg-cyan-400/15 text-cyan-300" : "hover:bg-white/5"}`}>
          <img src={p.avatar} alt="" className="w-6 h-6 rounded-full" />
          <span className="text-xs flex-1 truncate">{p.nickname}</span>
          {p._queue && <span className="text-[9px] px-1 py-0.5 rounded bg-cyan-400/20 text-cyan-300">chờ</span>}
          <span className="text-[10px] text-white/40">{p.elo}</span>
        </button>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-xl bg-[#0c0d14] border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold flex items-center gap-2"><Swords size={16} className="text-cyan-300" /> Ghép trận · Bàn {pairing.table.number}</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/5 rounded"><X size={16} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">Người chơi A</div>
            <Pick value={a} onChange={setA} exclude={b} testid="pair-pick-a" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">Người chơi B</div>
            <Pick value={b} onChange={setB} exclude={a} testid="pair-pick-b" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">Best Of</div>
            <select value={bo} onChange={(e) => setBo(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm">
              <option value={1}>BO1</option><option value={3}>BO3</option><option value={5}>BO5</option>
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">Chế độ</div>
            <select value={mode} onChange={(e) => setMode(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm">
              <option value="rank">Rank</option><option value="friendly">Giao hữu</option>
            </select>
          </div>
        </div>
        <button onClick={submit} data-testid="pair-submit" className="w-full py-2.5 rounded-md font-display font-semibold text-sm bg-gradient-to-r from-cyan-400 to-purple-500 text-black flex items-center justify-center gap-2">
          <Swords size={15} /> Bắt đầu trận
        </button>
      </div>
    </div>
  );
};

export default QueuePanel;
